import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Box,
  Input,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import { isBlank, notBlank } from "../util/ext";
import Grid from "@material-ui/core/Grid";
import {
  map,
  find,
  lensProp,
  filter,
  propEq,
  set,
  pluck,
  union,
  reduce,
  difference,
  mergeRight,
  mergeLeft,
  lift,
  project,
  partial,
  flip,
  assoc,
  pick,
} from "ramda";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import SubscriptionTypeSelect from "./SubscriptionTypeSelect";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import LuxonUtils from "@date-io/luxon";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import { Skeleton } from "@material-ui/lab";
import Typography from "@material-ui/core/Typography";
import { useModuleGroups } from "../manage-module-groups/moduleGroupsService";
import {
  useSubscriptionCreate,
  useSubscriptionUpdate,
} from "./subscriptionService";
import InputLabel from "@material-ui/core/InputLabel";
import InputAdornment from "@material-ui/core/InputAdornment";
import { matchSorter } from "match-sorter";
import { ListboxComponent } from "./ModuleAutoComplete";

const useStyles = makeStyles(theme => ({
  errorContainer: {
    marginTop: theme.spacing(1),
    fontWeight: "bold",
    color: theme.palette.error.main,
  },

  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },

  selectEmpty: {
    marginTop: theme.spacing(2),
  },

  title: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },

  multiPicker: {
    "& label.MuiFormLabel-root": {
      backgroundColor: "#fff",
      marginLeft: "-5px",
      padding: "0 6px",
    },
  },
}));

const moduleFilterOptions = (options, { inputValue }) =>
  matchSorter(options, inputValue, {
    keys: ["name", "displayName", "id", "status"],
  });

const PERPETUAL_END_DATE = "4000-01-01T00:00:00Z";

const initialFields = {
  type: "REGULAR",
  startDate: null,
  endDate: null,
  salesPrice: 0.0,
  perpetual: false,
};

const SubscriptionDialog = ({
  open,
  organizationId,
  subscription,
  moduleList,
  preselectedModules = [],
  organizationList,
  onSave,
  onClose,
  useAllModules = false,
}) => {
  const classes = useStyles();
  const [errors, setErrors] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [selectedModules, setSelectedModules] = useState(preselectedModules);
  const [selectedModuleGroups, setSelectedModuleGroups] = useState([]);
  const [fields, setFields] = useState(initialFields);

  const moduleGroupFetch = useModuleGroups();
  const [moduleGroups, setModuleGroups] = useState([]);

  const [dialogWidth, setDialogWidth] = useState("md");
  const [fullScreen, setFullScreen] = useState(false);

  const findOrganization = useCallback(
    orgId => {
      return find(propEq("organizationId", orgId), organizationList);
    },
    [organizationList],
  );

  const findModule = useCallback(
    moduleId => {
      return find(propEq("id", moduleId), moduleList);
    },
    [moduleList],
  );

  useEffect(() => {
    if (open && notBlank(organizationList) && notBlank(moduleList)) {
      setOrganizations(filter(org => org.active, organizationList));
      const modules = useAllModules
        ? moduleList
        : filter(propEq("status", "ACTIVE"), moduleList);

      setModules(modules);

      if (notBlank(subscription)) {
        setFields(
          pick(
            ["type", "startDate", "endDate", "salesPrice", "perpetual"],
            subscription,
          ),
        );

        const org = findOrganization(subscription.organizationId);
        setSelectedOrganizations([org]);

        const module = findModule(subscription.mediaId);
        setSelectedModules([module]);
      } else {
        setFields(initialFields);
        if (notBlank(organizationId)) {
          const org = findOrganization(organizationId);
          setSelectedOrganizations([org]);
        } else {
          setSelectedOrganizations([]);
        }
      }
    }
  }, [
    open,
    organizationList,
    moduleList,
    organizationId,
    findOrganization,
    findModule,
    subscription,
    useAllModules,
  ]);

  useEffect(() => {
    if (open && moduleGroupFetch.isSuccess && notBlank(moduleGroupFetch.data)) {
      setModuleGroups(moduleGroupFetch.data);
    }
  }, [open, moduleGroupFetch.isSuccess, moduleGroupFetch.data]);

  const onFieldChanged = field => value => {
    const val = value && value.target ? value.target.value : value;
    const update = set(lensProp(field), val, fields);
    setFields(update);
  };

  const onPerpetualChanged = useCallback(
    event => {
      const perpetual = event.target.checked;
      const definiteEnd = notBlank(subscription) ? subscription.endDate : null;
      const endDate = perpetual ? PERPETUAL_END_DATE : definiteEnd;

      setFields(mergeLeft({ perpetual, endDate }));
    },
    [subscription],
  );

  const handleOrganizationsChange = useCallback((_event, orgs) => {
    setSelectedOrganizations(orgs);
  }, []);

  const handleModulesChange = useCallback((_event, modules) => {
    setSelectedModules(modules);
  }, []);

  useEffect(() => {
    if (selectedModules.length > 100) {
      setFullScreen(true);
    } else {
      setFullScreen(false);
      if (selectedModules.length > 80) {
        setDialogWidth("xl");
      } else if (selectedModules.length > 60) {
        setDialogWidth("lg");
      } else if (selectedModules.length > 30) {
        setDialogWidth("md");
      } else {
        setDialogWidth("sm");
      }
    }
  }, [selectedModules]);

  const handleModuleGroupsChange = useCallback(
    (_event, moduleGroups) => {
      setSelectedModuleGroups(moduleGroups);

      const diff = difference(selectedModuleGroups, moduleGroups);
      if (notBlank(diff)) {
        const modules = map(
          id => find(propEq("id", id), moduleList),
          pluck("moduleId", reduce(union, [], pluck("modules", diff))),
        );

        setSelectedModules(prev => difference(prev, modules));
      } else {
        const modules = map(
          id => find(propEq("id", id), moduleList),
          pluck("moduleId", reduce(union, [], pluck("modules", moduleGroups))),
        );
        setSelectedModules(prev => union(prev, modules));
      }
    },
    [selectedModuleGroups, moduleList],
  );

  const validate = useCallback(() => {
    const errorList = [];
    if (isBlank(selectedOrganizations)) {
      errorList.push("At least one Organization must be selected.");
    }
    if (isBlank(selectedModules)) {
      errorList.push("At least one Module must be selected.");
    }
    if (isBlank(fields.startDate)) {
      errorList.push("Start Date is required.");
    }
    if (isBlank(fields.endDate)) {
      errorList.push("End Date is required.");
    }

    return errorList;
  }, [selectedOrganizations, selectedModules, fields]);

  const createSubscription = useSubscriptionCreate();
  const updateSubscription = useSubscriptionUpdate();
  const handleApply = useCallback(async () => {
    const errorList = validate();
    if (isBlank(errorList)) {
      const xProdObj = lift(mergeRight);
      const zipMediaId = lift(partial(flip(partial(assoc, ["mediaId"])), [{}]));

      try {
        const subs = map(
          mergeRight(fields),
          xProdObj(
            project(["organizationId"], selectedOrganizations),
            zipMediaId(pluck(["id"], selectedModules)),
          ),
        );

        if (isBlank(subscription)) {
          await Promise.all(
            map(sub => createSubscription.mutateAsync({ data: sub }), subs),
          );
        } else {
          await Promise.all(
            map(
              sub =>
                updateSubscription.mutateAsync({
                  resourceIds: { id: subscription.id },
                  data: sub,
                }),
              subs,
            ),
          );
        }

        onSave(subs);
      } catch (err) {
        if (notBlank(err.body)) {
          setErrors(pluck("message", err.body.errors));
        } else {
          setErrors([err.message]);
        }
      }
    } else {
      setErrors(errorList);
    }
  }, [
    validate,
    selectedModules,
    selectedOrganizations,
    fields,
    subscription,
    createSubscription,
    updateSubscription,
    onSave,
  ]);

  const handleCancel = useCallback(() => onClose(), [onClose]);

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth={true}
      maxWidth={dialogWidth}
      open={open}
      aria-labelledby="subscription-dialog-title"
    >
      <DialogTitle id="subscription-dialog-title">Add Subscription</DialogTitle>
      <DialogContent>
        {notBlank(errors) && (
          <div className={classes.errorContainer}>
            {errors.map(err => (
              <div key={Math.random()}>{err}</div>
            ))}
          </div>
        )}
        <Box>
          <MuiPickersUtilsProvider utils={LuxonUtils}>
            <Grid alignItems="flex-end" container spacing={5}>
              <Grid item sm={12}>
                <Box>
                  <Typography variant="h5">
                    Select one or more Organizations.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                {notBlank(organizations) ? (
                  <Box className={classes.multiPicker}>
                    <Autocomplete
                      multiple
                      id="organizations"
                      options={organizations}
                      getOptionLabel={option => option.organizationName}
                      value={selectedOrganizations || []}
                      filterSelectedOptions
                      onChange={handleOrganizationsChange}
                      disabled={
                        notBlank(subscription) &&
                        notBlank(subscription.organizationId)
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Organizations"
                          placeholder="Select..."
                        />
                      )}
                    />
                  </Box>
                ) : (
                  <Skeleton variant="text" />
                )}
              </Grid>

              <Grid item sm={12}>
                <Box mt={2}>
                  <Typography variant="h5">
                    Select one or more Modules.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                {notBlank(moduleGroups) ? (
                  <Box className={classes.multiPicker}>
                    <Autocomplete
                      multiple
                      id="moduleGroups"
                      options={moduleGroups}
                      getOptionLabel={option =>
                        notBlank(option) ? option.moduleGroupName : ""
                      }
                      value={selectedModuleGroups || []}
                      filterSelectedOptions
                      onChange={handleModuleGroupsChange}
                      disabled={
                        notBlank(subscription) && notBlank(subscription.mediaId)
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Module Groups"
                          placeholder="Select..."
                        />
                      )}
                    />
                  </Box>
                ) : (
                  <Skeleton variant="text" />
                )}
              </Grid>

              <Grid item sm={12}>
                {notBlank(modules) ? (
                  <Box className={classes.multiPicker}>
                    <Autocomplete
                      multiple
                      id="modules"
                      options={modules}
                      ListboxComponent={ListboxComponent}
                      getOptionLabel={option =>
                        notBlank(option) ? option.name : ""
                      }
                      value={selectedModules || []}
                      filterSelectedOptions
                      filterOptions={moduleFilterOptions}
                      onChange={handleModulesChange}
                      disabled={
                        notBlank(subscription) && notBlank(subscription.mediaId)
                      }
                      renderOption={(option, { selected }) => (
                        <Box width="100%">
                          <Box fontSize="1.4rem">
                            <Typography
                              noWrap
                              color={
                                option.status === "INACTIVE"
                                  ? "error"
                                  : "inherit"
                              }
                            >
                              {option.name}
                            </Typography>
                          </Box>
                          <Box fontSize="1.2rem">
                            <Typography
                              color={
                                option.status === "INACTIVE"
                                  ? "error"
                                  : "inherit"
                              }
                            >{`(${option.id})`}</Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Modules"
                          placeholder="Select..."
                        />
                      )}
                    />
                  </Box>
                ) : (
                  <Skeleton variant="text" />
                )}
              </Grid>

              <Grid item sm={12}>
                <Box mt={2}>
                  <Typography variant="h5">
                    Enter remaining subscription details.
                  </Typography>
                </Box>
              </Grid>
              <Grid item sm={6}>
                <KeyboardDatePicker
                  disableToolbar
                  fullWidth
                  autoOk
                  id="startDate"
                  format="MM/dd/yyyy"
                  label="Start Date"
                  variant="inline"
                  value={fields.startDate}
                  onChange={onFieldChanged("startDate")}
                  KeyboardButtonProps={{
                    "aria-label": "change start date",
                  }}
                />
              </Grid>

              <Grid item sm={6}>
                {!fields.perpetual && (
                  <KeyboardDatePicker
                    disableToolbar
                    fullWidth
                    autoOk
                    id="endDate"
                    format="MM/dd/yyyy"
                    label="End Date"
                    variant="inline"
                    maxDate="01/01/6000"
                    value={fields.endDate}
                    onChange={onFieldChanged("endDate")}
                    KeyboardButtonProps={{
                      "aria-label": "change end date",
                    }}
                  />
                )}
              </Grid>

              <Grid item sm={6}>
                <SubscriptionTypeSelect
                  value={fields.type}
                  label="Subscription Type"
                  required
                  onChange={onFieldChanged("type")}
                />
              </Grid>

              <Grid item sm={6}>
                <FormControl fullWidth className={classes.margin}>
                  <InputLabel htmlFor="salesPrice">Sales Price</InputLabel>
                  <Input
                    id="salesPrice"
                    value={fields.salesPrice}
                    onChange={onFieldChanged("salesPrice")}
                    startAdornment={
                      <InputAdornment position="start">$</InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item sm={12}>
                <FormControl fullWidth={true} className={classes.formControl}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={fields.perpetual}
                        onChange={onPerpetualChanged}
                        name="perpetual"
                        color="primary"
                      />
                    }
                    label="Perpetual"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionDialog;
